/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactNode, useEffect, useState } from 'react';
import { LocalStorageService } from '~/core/utils/storage';

interface TabTimeElapsedProps {
  onLeaveTab?: (time: number) => void;
  children?: ReactNode;
}

const INITIAL_TIME_INCREMENT = 10;
const LIMIT_INCREMENTAL_TIME = 300;

const TabTimeElapsed: React.FC<TabTimeElapsedProps> = ({
  onLeaveTab,
  children,
}) => {
  const [timeElapsed, setTimeElapsed] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(true);

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      setIsActive(false);
      if (onLeaveTab) {
        onLeaveTab(timeElapsed);
      }
    } else {
      setIsActive(true);
    }
  };

  const handleBeforeUnload = () => {
    if (onLeaveTab) {
      onLeaveTab(timeElapsed);
    }
  };

  // this funcion checks if a time matches with the increased gradually times
  const requestTime = (time: number) => {
    let current = INITIAL_TIME_INCREMENT;
    let increment = INITIAL_TIME_INCREMENT;

    while (current <= time) {
      if (current === time) {
        return true;
      }
      current += increment;
      increment +=
        current <= LIMIT_INCREMENTAL_TIME ? INITIAL_TIME_INCREMENT : 0;
    }
    return false;
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (isActive) {
      interval = setInterval(() => {
        setTimeElapsed((prevTime) => prevTime + 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isActive]);

  useEffect(() => {
    const isTimeIntervalReached: boolean =
      timeElapsed !== 0 && requestTime(timeElapsed);

    if (isActive && isTimeIntervalReached && onLeaveTab) {
      onLeaveTab(timeElapsed);
    }
  }, [isActive, timeElapsed]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  useEffect(() => {
    LocalStorageService.setItem('timeElapsed', timeElapsed.toString());
  }, [timeElapsed]);

  return <div data-testid="time-elapsed-component">{children}</div>;
};

export default TabTimeElapsed;
